import {
  ArrowLeft,
  ArrowRight,
  ExternalLink,
  Home,
  RotateCw,
} from "lucide-react";
import type { FormEvent, ReactNode } from "react";
import { useEffect, useState } from "react";
import { normalizeBrowserUrl } from "./browser-url";

interface BrowserControlsProps {
  baseUrl: string;
  currentUrl: string;
  canGoBack: boolean;
  canGoForward: boolean;
  onBack: () => void;
  onForward: () => void;
  onReload: () => void;
  onNavigate: (url: string) => void;
}

export function BrowserControls({
  baseUrl,
  currentUrl,
  canGoBack,
  canGoForward,
  onBack,
  onForward,
  onReload,
  onNavigate,
}: BrowserControlsProps) {
  const [draftUrl, setDraftUrl] = useState(currentUrl);

  useEffect(() => {
    setDraftUrl(currentUrl);
  }, [currentUrl]);

  const submitUrl = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onNavigate(normalizeBrowserUrl(draftUrl, baseUrl));
  };

  return (
    <div className="flex h-9 shrink-0 items-center gap-1 border-b border-[var(--fintheon-accent)]/10 bg-black/35 px-2 backdrop-blur">
      <IconButton label="Back" disabled={!canGoBack} onClick={onBack}>
        <ArrowLeft size={13} />
      </IconButton>
      <IconButton label="Forward" disabled={!canGoForward} onClick={onForward}>
        <ArrowRight size={13} />
      </IconButton>
      <IconButton label="Reload" onClick={onReload}>
        <RotateCw size={12} />
      </IconButton>
      <IconButton label="Home" onClick={() => onNavigate(baseUrl)}>
        <Home size={12} />
      </IconButton>
      <form onSubmit={submitUrl} className="min-w-0 flex-1">
        <input
          value={draftUrl}
          onChange={(event) => setDraftUrl(event.target.value)}
          spellCheck={false}
          aria-label="Browser URL"
          className="h-6 w-full rounded border border-[var(--fintheon-accent)]/10 bg-black/45 px-2 font-mono text-[10px] text-[var(--fintheon-text)]/70 outline-none transition-colors placeholder:text-[var(--fintheon-muted)] focus:border-[var(--fintheon-accent)]/35"
        />
      </form>
      <IconButton
        label="Open externally"
        onClick={() => window.electron?.openExternal?.(currentUrl)}
      >
        <ExternalLink size={12} />
      </IconButton>
    </div>
  );
}

function IconButton({
  label,
  disabled = false,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="grid h-6 w-6 place-items-center rounded border border-transparent text-[var(--fintheon-muted)] transition-colors hover:border-[var(--fintheon-accent)]/15 hover:text-[var(--fintheon-accent)] disabled:pointer-events-none disabled:opacity-25"
    >
      {children}
    </button>
  );
}
